import token from 'utils/token';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { buildPost, refreshToken } from 'api/builders';
import camelizeKeys from 'utils/camelizeKeys';

const NAME = 'auth';

const initialState = {
  loading: false,
  error: null,
  authorized: false,
};

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, thunkAPI) => {
    try {
      await refreshToken();
      return token.isValid;
    }
    catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const authLogin = createAsyncThunk(
  'auth/login',
  async (payload, thunkAPI) => {
    try {
      const { email, password } = payload;
      const body = {
        email,
        password,
        grant_type: 'password',
      };
      const response = await buildPost({
        url: 'oauth/token',
        credentials: 'include',
      })(body, async (response) => response);

      if (response.status < 400) {
        const json = await response.json();
        return { ...json, status: response.status };
      }
      return { status: response.status };
    }
    catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

const slice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    setAuthorized: (state, action) => {
      state.authorized = action.payload;
      if (!action.payload) {
        token.reset();
      }
    },
  },
  extraReducers: {
    [authLogin.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [authLogin.fulfilled]: (state, action) => {
      state.loading = false;
      const {
        accessToken, createdAt, expiresIn, status,
      } = camelizeKeys(action.payload);
      if (status === 200) {
        token.refresh({ accessToken, createdAt, expiresIn });
        state.authorized = true;
      }
      else {
        state.authorized = false;
      }
    },
    [authLogin.rejected]: (state, action) => {
      state.authorized = false;
      state.loading = false;
      state.error = action.payload;
    },

    [validateToken.pending]: (state) => {
      state.loading = true;
    },
    [validateToken.rejected]: (state, action) => {
      state.error = action.payload;
      state.authorized = false;
      state.loading = false;
    },
    [validateToken.fulfilled]: (state, action) => {
      const isValid = action.payload;
      state.authorized = isValid;
      state.loading = false;
      state.error = null;
    },
  },
});

export const { setAuthorized } = slice.actions;

export const selectAuth = (state) => state[NAME];
export const selectAuthorized = (state) => state[NAME].authorized;
export const selectAuthLoading = (state) => state[NAME].loading;

export default slice.reducer;
